import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, SelectElement } from 'react-hook-form-mui';
import { useHistory, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useTheme } from '@mui/material/styles';
import useYupValidationResolver from '../utils/formUtils/yupValidationResolver';
import {
  useGetCustomMetricQuery,
  useGetCustomReportsByMetricQuery,
  useGetCustomReportsDatasetsQuery,
} from '../admin/requests/queries';
import { useCustomMetricSaveMutation, useCustomMetricUpdateMutation } from '../admin/requests/mutations';
import { CustomMetric, CustomMetricData } from '../admin/requests/index';
import { useSnackbar } from '../contexts/SnackbarContext';
import routes from '../constants/routes';
import FormFooter from '../components/StickyFooter/FormFooter';
import UnsavedFormDataPrompt from '../components/UnsavedDataPrompt/UnsavedFormDataPrompt';
import CustomMetricsSkeleton from './CustomMetricsSkeleton';
import FormulaBuilder, { FormulaBuilderRef, FormulaToken } from './FormulaBuilder/FormulaBuilder';
import CharCountTextField from './CharCountTextField';
import UpdateMetricConfirmationDialog from './UpdateMetricConfirmationDialog';
import {
  rootBoxSx,
  headerBoxSx,
  titleSx,
  paperSx,
  sectionBoxSx,
  sectionTitleSx,
  subtextSx,
  formLabelSx,
  previewBoxSx,
  previewMetricNameSx,
  previewValueSx,
  datasetSwitchDialogContentSx,
  breadcrumbsSx,
  breadcrumbLinkSx,
  breadcrumbTextSx,
} from './styles';
import { getFormattedValue } from '../../sales/utilities';

export enum MetricType {
  NUMBER = 'number',
  CURRENCY = 'currency',
  PERCENTAGE = 'percentage',
}

export enum FormatType {
  TENTH = 'tenth',
  HUNDREDTH = 'hundredth',
}

interface CustomMetricFormData {
  metricName: string;
  dataSet: string;
  description: string;
  metricType: MetricType;
  formatType: FormatType;
  formulaTokens: FormulaToken[];
  selectedMetric?: Array<{ label: string; value: string }>;
  editMetric?: Array<{ label: string; value: string }>;
}

// Map metricType and formatType to API format
const METRIC_TYPE_TO_API_MAP = {
  [MetricType.NUMBER]: 'Number',
  [MetricType.CURRENCY]: 'Currency',
  [MetricType.PERCENTAGE]: 'Percentage',
} as const;

const FORMAT_TYPE_TO_API_MAP = {
  [FormatType.TENTH]: 'DecimalToNearest10',
  [FormatType.HUNDREDTH]: 'DecimalToNearest100',
} as const;

const buildCreatePayload = (data: CustomMetricFormData): CustomMetricData => ({
  name: data.metricName,
  formulaTokens: data.formulaTokens,
  dataset: data.dataSet,
  description: data.description || '',
  metricType: METRIC_TYPE_TO_API_MAP[data.metricType],
  format: FORMAT_TYPE_TO_API_MAP[data.formatType],
});

const buildUpdatePayload = (data: CustomMetricFormData, customMetric?: CustomMetric): Partial<CustomMetricData> => {
  const payload: Partial<CustomMetricData> = {};

  if (!customMetric) {
    return payload;
  }

  if (data.metricName !== customMetric.name) {
    payload.name = data.metricName;
  }

  if (data.description !== (customMetric.description || '')) {
    payload.description = data.description || '';
  }

  if (data.dataSet !== customMetric.dataset) {
    payload.dataset = data.dataSet;
  }

  const currentMetricType = METRIC_TYPE_TO_API_MAP[data.metricType];
  if (currentMetricType !== customMetric.metricType) {
    payload.metricType = currentMetricType;
  }

  const currentFormatType = FORMAT_TYPE_TO_API_MAP[data.formatType];
  if (currentFormatType !== customMetric.format) {
    payload.format = currentFormatType;
  }

  const formulaChanged = !isEqual(data.formulaTokens, customMetric.formulaTokens || []);
  if (formulaChanged) {
    payload.formulaTokens = data.formulaTokens;
    // Include dataset when formula changes as tokens reference the dataset
    payload.dataset = data.dataSet;
  }

  return payload;
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getValidationMessage = (error: any): string | undefined => {
  const constraintViolations = error?.details?.response?.constraintViolations || error?.details?.constraintViolations;

  if (!constraintViolations || !Array.isArray(constraintViolations)) {
    return undefined;
  }

  return constraintViolations[0]?.message;
};

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
const getSaveErrorMessage = (error: any, fallbackMessage: string): string => {
  const statusCode = error?.details?.response?.statusCode;
  const errorMessage = error?.details?.response?.message;
  const isDuplicateError = statusCode === 409;

  return isDuplicateError && errorMessage ? errorMessage : fallbackMessage;
};

function CustomMetrics() {
  const { t } = useTranslation();
  const { metricId } = useParams<{ metricId?: string }>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { setSnackbarState } = useSnackbar();
  const theme = useTheme();

  // Determine if creating new metric (metricId is '0') or editing existing
  const isNewMetric = !metricId || metricId === '0';

  const [formulaError, setFormulaError] = useState<string>('');
  const [attemptedCloseWhileDirty, setAttemptedCloseWhileDirty] = useState(false);
  const [showDatasetChangeDialog, setShowDatasetChangeDialog] = useState(false);
  const [pendingDataset, setPendingDataset] = useState<string>('');
  const [previousDataset, setPreviousDataset] = useState<string>('');
  const [showUpdateConfirmationDialog, setShowUpdateConfirmationDialog] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<CustomMetricFormData | null>(null);
  const formulaBuilderRef = useRef<FormulaBuilderRef>(null);
  // Expose ref for test access
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'test') {
    // @ts-expect-error: test-only property
    // eslint-disable-next-line no-underscore-dangle
    window._formulaBuilderRef = formulaBuilderRef;
  }

  // Mutations for saving and updating custom metric
  const saveMetricMutation = useCustomMetricSaveMutation();
  const updateMetricMutation = useCustomMetricUpdateMutation();

  // Fetch datasets from API
  const { data: datasets, isLoading: isDatasetsLoading } = useGetCustomReportsDatasetsQuery();

  // Fetch specific metric if metricId is provided and not '0' (edit mode)
  const { data: customMetric, isLoading: isCustomMetricLoading } = useGetCustomMetricQuery(
    !isNewMetric,
    metricId || ''
  );

  // Fetch custom reports for the metric (only when editing existing metric)
  const { data: reports = [], isLoading } = useGetCustomReportsByMetricQuery(
    !isNewMetric && !!metricId,
    metricId || ''
  );

  const canEditDataset = isNewMetric || (!isLoading && reports.length === 0);

  // Transform datasets to dropdown options using a stable id
  const dataSetOptions = datasets
    ? datasets.map((dataset) => ({
        id: String(dataset.datasetName),
        title: dataset.datasetName,
      }))
    : [];

  const validationSchema = yup.object({
    metricName: yup
      .string()
      .trim()
      .required(t('formMessage.required'))
      .max(50, t('inventory.recipeDetailsPage.nameHelperText', { length: 0 })),
    dataSet: yup.string().required(t('formMessage.required')),
    description: yup
      .string()
      .trim()
      .max(250, t('inventory.recipeDetailsPage.nameHelperText', { length: 0 })),
  });

  const formContext = useForm<CustomMetricFormData>({
    defaultValues: {
      metricName: '',
      dataSet: '',
      description: '',
      metricType: MetricType.NUMBER,
      formatType: FormatType.TENTH,
      formulaTokens: [],
      selectedMetric: [],
      editMetric: [],
    },
    resolver: useYupValidationResolver(validationSchema),
    mode: 'onChange',
  });

  const {
    formState: { isDirty },
    reset,
    setValue,
    watch,
  } = formContext;

  // Watch form values to use in handlers
  const metricType = watch('metricType');
  const formatType = watch('formatType');

  // Populate form with custom metric data when in edit mode
  useEffect(() => {
    if (customMetric) {
      // Map API metric type to component MetricType enum
      const metricTypeMap: Record<string, MetricType> = {
        Number: MetricType.NUMBER,
        Currency: MetricType.CURRENCY,
        Percentage: MetricType.PERCENTAGE,
      };

      // Map API format to component FormatType enum
      const formatTypeMap: Record<string, FormatType> = {
        DecimalToNearest10: FormatType.TENTH,
        DecimalToNearest100: FormatType.HUNDREDTH,
      };

      const loadedMetricType = metricTypeMap[customMetric.metricType] || MetricType.NUMBER;
      const loadedFormatType = formatTypeMap[customMetric.format] || FormatType.TENTH;
      const loadedFormulaTokens = customMetric.formulaTokens || [];

      // Reset form with ALL loaded values - this establishes the baseline for isDirty
      reset({
        metricName: customMetric.name || '',
        description: customMetric.description || '',
        dataSet: customMetric.dataset || '',
        metricType: loadedMetricType,
        formatType: loadedFormatType,
        formulaTokens: loadedFormulaTokens,
        selectedMetric: [],
        editMetric: [],
      });

      // Set formula tokens in the builder
      if (loadedFormulaTokens.length > 0 && formulaBuilderRef.current) {
        formulaBuilderRef.current.setFormulaTokens(loadedFormulaTokens);
      }

      // Set previous dataset to current dataset
      setPreviousDataset(customMetric.dataset || '');
    }
  }, [customMetric, reset]);

  // Watch the selected dataset to get its columns/metrics
  const selectedDataSet = formContext.watch('dataSet');

  // Handle dataset changes - show dialog if formula exists
  useEffect(() => {
    if (!selectedDataSet || selectedDataSet === previousDataset) {
      return;
    }

    // Check if there's an active formula
    const formulaTokens: FormulaToken[] = formulaBuilderRef.current?.getFormulaTokens() || [];
    const hasFormula = formulaTokens && formulaTokens.length > 0;

    if (hasFormula && previousDataset) {
      // Show confirmation dialog
      setPendingDataset(selectedDataSet);
      setShowDatasetChangeDialog(true);
      // Revert the dataset selection temporarily
      formContext.setValue('dataSet', previousDataset, { shouldValidate: false });
    } else {
      // No formula or first selection, allow the change
      setPreviousDataset(selectedDataSet);
    }
  }, [selectedDataSet, previousDataset, formContext]);

  // Get metrics from the selected dataset's columns
  const availableMetrics =
    selectedDataSet && datasets
      ? datasets
          .find((dataset) => dataset.datasetName === selectedDataSet)
          ?.columns.filter((column) => column.isNumeric)
          .map((column) => ({
            id: String(column.id),
            label: column.columnName,
          })) || []
      : [];

  const handleMetricTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('metricType', event.target.value as MetricType, { shouldDirty: true });
  };

  const handleFormatTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue('formatType', event.target.value as FormatType, { shouldDirty: true });
  };

  const handleDatasetChangeCancel = () => {
    setShowDatasetChangeDialog(false);
    setPendingDataset('');
  };

  const handleDatasetChangeConfirm = () => {
    // Clear the formula (this will trigger onChange with empty array)
    formulaBuilderRef.current?.clearFormula();
    // Also explicitly update form field to ensure sync
    formContext.setValue('formulaTokens', [], { shouldDirty: true });
    // Apply the pending dataset change
    formContext.setValue('dataSet', pendingDataset, { shouldValidate: true });
    setPreviousDataset(pendingDataset);
    // Close dialog
    setShowDatasetChangeDialog(false);
    setPendingDataset('');
  };

  const handleCancel = () => {
    if (isDirty) {
      setAttemptedCloseWhileDirty(true);
    } else {
      // If form is clean, navigate directly
      history.push(routes.CUSTOM_METRICS);
    }
  };

  const onClickConfirm = () => {
    setAttemptedCloseWhileDirty(false);
    // Navigate back to custom metrics list
    history.push(routes.CUSTOM_METRICS);
  };

  const onClickCancel = () => {
    setAttemptedCloseWhileDirty(false);
  };

  const performSave = async (data: CustomMetricFormData) => {
    // For update (PATCH), only send changed fields. For create (POST), send all fields.
    const payload: Partial<CustomMetricData> = isNewMetric
      ? buildCreatePayload(data)
      : buildUpdatePayload(data, customMetric);

    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    const handleOnError = (error: any) => {
      const validationMessage = getValidationMessage(error);
      if (validationMessage) {
        setSnackbarState({
          open: true,
          message: validationMessage,
          color: 'error',
        });
        return;
      }

      // Show appropriate error message
      setSnackbarState({
        open: true,
        message: getSaveErrorMessage(error, t('navItems.customMetricsPage.saveError')),
        color: 'error',
      });
    };

    // Save or update custom metric via API
    try {
      if (isNewMetric) {
        await saveMetricMutation.mutateAsync({ customMetric: payload as CustomMetricData });
      } else if (metricId) {
        await updateMetricMutation.mutateAsync({ id: metricId, customMetric: payload as CustomMetricData });
      } else {
        throw new Error('Metric ID is missing for update operation.');
      }

      // Close confirmation dialog if it was open
      setShowUpdateConfirmationDialog(false);
      setPendingUpdateData(null);

      // Reset form to clear dirty state BEFORE navigation
      reset(
        {
          metricName: data.metricName,
          dataSet: data.dataSet,
          description: data.description,
          metricType: data.metricType,
          formatType: data.formatType,
          formulaTokens: data.formulaTokens,
          selectedMetric: [],
          editMetric: [],
        },
        { keepDefaultValues: false }
      );
      // Invalidate queries to refresh the grid data
      queryClient.invalidateQueries({ queryKey: ['allCustomMetrics'] });
      if (!isNewMetric) {
        queryClient.invalidateQueries({ queryKey: ['customMetricById', metricId] });
      }

      // Show success message
      setSnackbarState({
        open: true,
        message: isNewMetric
          ? t('navItems.customMetricsPage.saveSuccess')
          : t('navItems.customMetricsPage.updateSuccess'),
        color: 'success',
      });

      // Navigate to custom metrics list using setTimeout to ensure state updates
      setTimeout(() => {
        history.push(routes.CUSTOM_METRICS);
      }, 0);
    } catch (error) {
      handleOnError(error);
    }
  };

  const handleUpdateConfirmationCancel = () => {
    setShowUpdateConfirmationDialog(false);
    setPendingUpdateData(null);
  };

  const handleUpdateConfirmationConfirm = async () => {
    if (pendingUpdateData) {
      await performSave(pendingUpdateData);
    }
  };

  const onSubmit = async (data: CustomMetricFormData) => {
    // Get formula tokens from form data (they're kept in sync)
    const { formulaTokens } = data;
    // Check if formula is empty
    if (formulaTokens.length === 0) {
      setFormulaError(t('navItems.customMetricsPage.formulaRequired'));
      return;
    }

    // Validate formula before submission
    const isFormulaValid = formulaBuilderRef.current?.validateFormula();
    if (!isFormulaValid) {
      return;
    }

    // Clear any previous errors
    setFormulaError('');

    // If editing an existing metric that's in use, show confirmation dialog
    if (!isNewMetric && !isLoading && reports.length > 0) {
      setPendingUpdateData(data);
      setShowUpdateConfirmationDialog(true);
      return;
    }

    // Otherwise proceed with save
    await performSave(data);
  };

  return (
    <Box sx={rootBoxSx}>
      <Box>
        {isDatasetsLoading || (!isNewMetric && isCustomMetricLoading) ? (
          <CustomMetricsSkeleton />
        ) : (
          <>
            <Grid container flexDirection='row'>
              <Breadcrumbs
                sx={breadcrumbsSx}
                aria-label='custom-metrics-breadcrumb'
                separator={<Typography variant='overline'>/</Typography>}
              >
                <Typography
                  component={Link}
                  to={routes.CUSTOM_METRICS}
                  variant='overline'
                  sx={breadcrumbLinkSx}
                  data-testid='custom-metrics-link'
                >
                  {t('navItems.customMetrics')}
                </Typography>
                <Typography variant='overline' sx={breadcrumbTextSx} data-testid='metric-name-breadcrumb'>
                  {isNewMetric ? t('navItems.customMetricsPage.title') : customMetric?.name || ''}
                </Typography>
              </Breadcrumbs>
            </Grid>

            <Box sx={headerBoxSx}>
              <Typography variant='h5' sx={titleSx} data-testid='page-title'>
                {isNewMetric
                  ? t('navItems.customMetricsPage.title')
                  : t('navItems.customMetricsPage.editMetric', { metricName: customMetric?.name })}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {t('global.requiredText')}
              </Typography>
            </Box>

            <FormContainer formContext={formContext} onSuccess={onSubmit}>
              <Paper elevation={0} sx={paperSx}>
                <Box sx={sectionBoxSx}>
                  <Typography variant='h6' sx={sectionTitleSx}>
                    {t('navItems.customMetricsPage.metricDetails')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <CharCountTextField
                        name='metricName'
                        label={t('navItems.customMetricsPage.metricName')}
                        maxLength={50}
                        warningThreshold={40}
                        required
                        testId='metric-name-input'
                      />
                    </Grid>

                    <Grid item xs={4}>
                      <SelectElement
                        name='dataSet'
                        label={t('navItems.customMetricsPage.dataSet')}
                        variant='standard'
                        fullWidth
                        disabled={!canEditDataset}
                        required
                        options={dataSetOptions}
                        inputProps={{ 'data-testid': 'data-set-select' }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value: unknown) => {
                            if (!value || value === '') {
                              return (
                                <span style={{ color: theme.palette.text.disabled }}>
                                  {t('navItems.customMetricsPage.selectDataSet')}
                                </span>
                              );
                            }
                            return value as string;
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={8}>
                      <CharCountTextField
                        name='description'
                        label={t('navItems.customMetricsPage.description')}
                        maxLength={250}
                        warningThreshold={240}
                        multiline
                        rows={2}
                        testId='description-input'
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              <FormulaBuilder
                ref={formulaBuilderRef}
                metrics={availableMetrics}
                disabled={!selectedDataSet}
                error={formulaError}
                onErrorChange={setFormulaError}
                onChange={(tokens) => {
                  // Receive fresh tokens from builder and update form state
                  setValue('formulaTokens', tokens, { shouldDirty: true });
                }}
                dataSet={selectedDataSet}
              />

              <Paper elevation={0} sx={paperSx}>
                <Box sx={sectionBoxSx}>
                  <Typography variant='h6' sx={sectionTitleSx}>
                    {t('navItems.customMetricsPage.metricDisplay')}
                  </Typography>
                  <Typography variant='body2' color='textSecondary' sx={subtextSx}>
                    {t('navItems.customMetricsPage.metricDisplaySubtext')}
                  </Typography>

                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControl component='fieldset'>
                        <FormLabel component='legend' required sx={formLabelSx}>
                          {t('navItems.customMetricsPage.metricType')}
                        </FormLabel>
                        <RadioGroup row value={metricType} onChange={handleMetricTypeChange}>
                          {Object.values(MetricType).map((type) => (
                            <FormControlLabel
                              key={type}
                              value={type}
                              control={<Radio />}
                              label={t(
                                `navItems.customMetricsPage.metricType${type.charAt(0).toUpperCase() + type.slice(1)}`
                              )}
                              data-testid={`metric-type-${type}`}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl component='fieldset'>
                        <FormLabel component='legend' required sx={formLabelSx}>
                          {t('navItems.customMetricsPage.format')}
                        </FormLabel>
                        <RadioGroup row value={formatType} onChange={handleFormatTypeChange}>
                          {Object.values(FormatType).map((type) => (
                            <FormControlLabel
                              key={type}
                              value={type}
                              control={<Radio />}
                              label={t(
                                `navItems.customMetricsPage.format${type.charAt(0).toUpperCase() + type.slice(1)}`
                              )}
                              data-testid={`format-type-${type}`}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Box sx={previewBoxSx}>
                        <Typography variant='body2' sx={previewMetricNameSx}>
                          {formContext.watch('metricName') || t('navItems.customMetricsPage.metricName')}
                        </Typography>
                        <Typography variant='body1' sx={previewValueSx} data-testid='formatted-value-display'>
                          {getFormattedValue(metricType, formatType)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* {!isViewMode && ( */}
              <FormFooter
                loading={saveMetricMutation.isLoading || updateMetricMutation.isLoading}
                buttons={[
                  {
                    onClick: handleCancel,
                    dataTestId: 'cancel-button',
                  },
                  {
                    name: isNewMetric ? 'buttonText.create' : 'buttonText.save',
                    dataTestId: isNewMetric ? 'create-button' : 'save-button',
                    submitType: true,
                    disabled:
                      !formContext.formState.isValid || !isDirty || formContext.watch('formulaTokens').length === 0,
                  },
                ]}
              />
              {/* )} */}

              <UnsavedFormDataPrompt
                open={attemptedCloseWhileDirty}
                onConfirm={onClickConfirm}
                onCancel={onClickCancel}
                when={isDirty}
                navigate={(path) => history.push(path)}
                shouldBlockNavigation={() => isDirty}
              />
            </FormContainer>

            {/* Dataset Change Confirmation Dialog */}
            <Dialog
              open={showDatasetChangeDialog}
              onClose={handleDatasetChangeCancel}
              maxWidth='xs'
              fullWidth
              data-testid='dataset-change-dialog'
            >
              <DialogTitle>{t('navItems.customMetricsPage.datasetSwitchDialogTitle')}</DialogTitle>
              <DialogContent sx={datasetSwitchDialogContentSx}>
                <Typography variant='body2'>{t('navItems.customMetricsPage.datasetSwitchDialogMessage')}</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDatasetChangeCancel} data-testid='dataset-change-cancel-button'>
                  {t('buttonText.cancel')}
                </Button>
                <Button
                  onClick={handleDatasetChangeConfirm}
                  variant='contained'
                  color='error'
                  data-testid='dataset-change-confirm-button'
                >
                  {t('navItems.customMetricsPage.datasetSwitchDialogConfirm')}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Update Metric Confirmation Dialog */}
            <UpdateMetricConfirmationDialog
              open={showUpdateConfirmationDialog}
              onCancel={handleUpdateConfirmationCancel}
              onConfirm={handleUpdateConfirmationConfirm}
              metricName={customMetric?.name || ''}
              metricId={metricId || ''}
              isSaving={updateMetricMutation.isLoading}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

export default CustomMetrics;
